import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Loader2, Play, X } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
const GRADIENT_URL = 'https://cdn.magicpatterns.com/uploads/2vwVNynGfhLb1mPWKRMXVM/appscreen_(2).png';
const APP_SCREEN_URL = 'https://cdn.magicpatterns.com/uploads/6opscxMxauK8u5Bs45318A/appscreen_(1).png';

// Contenu affiché dans la modale après le clic, selon la langue
const EMBED_URLS = {
  fr: 'https://project-motion-promo-685.magicpatterns.app',
  en: 'https://project-observant-pretzel-599.magicpatterns.app'
};
export function ProductShowcase() {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const {
    t,
    lang
  } = useLanguage();
  const embedUrl = EMBED_URLS[lang];
  useEffect(() => {
    if (isOpen) setIsLoaded(false);
  }, [isOpen]);
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen]);
  return <section aria-label={t.showcase.sectionLabel} className="px-4 pb-24 pt-2 sm:px-6 sm:pb-32 sm:pt-4 lg:pb-40" data-id="element-79" data-mp="2i9yisgnm9onz">
      <motion.div initial={{
      opacity: 0,
      y: 28
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.6,
      delay: 0.1,
      ease: [0.22, 1, 0.36, 1]
    }} className="mx-auto max-w-[1080px]" data-id="element-80" data-mp="1ljxyouy6zwd0">
        <button type="button" onClick={() => setIsOpen(true)} aria-label={t.showcase.play} className="group relative isolate block aspect-[1359/769] w-full overflow-hidden rounded-[clamp(14px,3.3vw,36px)] shadow-[0_24px_60px_rgba(8,8,82,0.18)] [transform:translateZ(0)] focus:outline-none focus-visible:ring-4 focus-visible:ring-orange-rewayz-500" data-id="element-81" data-mp="1ujb85nmbgkxq">
          {/* Fond dégradé */}
          <img src={GRADIENT_URL} alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover" data-id="element-82" data-mp="2a3utc1t3uy1j" />

          {/* Fenêtre de l'application (ombre incluse dans l'image), centrée */}
          <img src={APP_SCREEN_URL} alt={t.showcase.previewAlt} className="absolute left-1/2 top-1/2 w-[88%] max-w-none -translate-x-1/2 -translate-y-1/2 transition-transform duration-500 group-hover:scale-[1.01]" data-id="element-83" data-mp="2ew5sh2g0uft1" />

          <span className="absolute inset-0 flex items-center justify-center transition-colors group-hover:bg-[rgba(8,8,82,0.10)]" data-id="element-84" data-mp="1ma32fd12f849">
            <span className="flex h-16 w-16 items-center justify-center rounded-full bg-white shadow-[0_10px_30px_rgba(8,8,82,0.35)] ring-8 ring-white/30 transition-transform group-hover:scale-110 sm:h-20 sm:w-20" data-id="element-85" data-mp="1w5oclygafep3">
              <Play className="ml-1 h-6 w-6 fill-blue-reways-950-lock text-blue-reways-950-lock sm:h-7 sm:w-7" aria-hidden="true" data-id="element-86" data-mp="37k1oe4xro3or" />
            </span>
          </span>
        </button>
      </motion.div>

      <AnimatePresence data-id="element-87" data-mp="3k1cfofrypvnp">
        {isOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} transition={{
        duration: 0.2
      }} role="dialog" aria-modal="true" aria-label={t.showcase.dialogLabel} onClick={() => setIsOpen(false)} className="fixed inset-0 z-[100] flex items-center justify-center bg-[rgba(8,8,82,0.65)] p-4 backdrop-blur-sm" data-id="element-88" data-mp="357hkudon66vi">
            <motion.div initial={{
          opacity: 0,
          scale: 0.97
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0,
          scale: 0.97
        }} transition={{
          duration: 0.22,
          ease: [0.22, 1, 0.36, 1]
        }} onClick={e => e.stopPropagation()} className="relative h-[90vh] w-[90vw] overflow-hidden rounded-2xl bg-white shadow-[0_30px_80px_rgba(8,8,82,0.45)]" data-id="element-89" data-mp="3t4dhi49sp0lk">
              <iframe src={embedUrl} title={t.showcase.iframeTitle} onLoad={() => setIsLoaded(true)} allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen" allowFullScreen className="h-full w-full border-0" data-id="element-90" data-mp="2sw8d04cfh3ib" />

              <AnimatePresence data-id="element-91" data-mp="3bn7ygvkprv6u">
                {!isLoaded && <motion.div initial={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} transition={{
              duration: 0.3
            }} className="absolute inset-0 flex flex-col items-center justify-center gap-4 bg-grey-50" data-id="element-92" data-mp="3bq3w0isnghur">
                    <Loader2 className="h-10 w-10 animate-spin text-blue-reways-950-lock" aria-hidden="true" data-id="element-93" data-mp="3a0wx1fls1i1" />
                    <p role="status" aria-live="polite" className="text-sm text-grey-800-lock" data-id="element-94" data-mp="6rgd17g8tygy">
                      {t.showcase.loading}
                    </p>
                  </motion.div>}
              </AnimatePresence>
              <button type="button" onClick={() => setIsOpen(false)} className="absolute right-3 top-3 flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-blue-reways-950-lock transition-colors hover:text-blue-reways-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-orange-rewayz-500" data-id="element-95" data-mp="knabewje55ft">
                {t.showcase.close}
                <X className="h-5 w-5" aria-hidden="true" data-id="element-96" data-mp="18fhesfnjacoq" />
              </button>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}